/**
 * O que uma linha do histórico de classificação DIZ, em português.
 *
 * A trilha guarda `{campo: {old, new}}` com ids; até 2026-10-07 a tela
 * mostrava só o nome do campo ("Temas"), e a dona, ao ver o histórico
 * migrado do legado, perguntou "cadê a mudança que a pessoa fez?". Os
 * nomes dos ids vêm do servidor em `labels` (`{campo: {id: nome}}`,
 * resolvidos na leitura, inclusive de tema já desativado) — resolver
 * aqui exigiria o catálogo inteiro, e um tema REMOVIDO não está mais na
 * menção pra ser consultado.
 *
 * Puro e testado: recebe o diff e os rótulos, devolve uma frase por
 * campo. Id sem rótulo sai como `#id`, nunca some.
 */

import type { ClassificationEdit } from "@/shared/api/api-client";
import { polarityLabel } from "@/shared/types/polarity";
import { APPROACH_CHOICES } from "@/features/mentions/mention-filter-criteria";

export type EditLine = { field: string; label: string; text: string };

const FIELD_LABELS: Record<string, string> = {
  polarity: "Polaridade",
  approach: "Abordagem",
  theme_ids: "Subtemas",
  theme_percentages: "Peso dos subtemas",
  highlight_theme_id: "Subtema em destaque",
  reviewed: "Revisão",
  linked: "Vínculo manual",
  discarded: "Descarte",
  title_citation: "Citação no título",
  key_message_ids: "Mensagens-chave",
  spokesperson_ids: "Porta-vozes"
};

const ARROW = " → ";

function approachLabel(value: unknown): string {
  if (value === null || value === undefined) return "sem abordagem";
  const found = APPROACH_CHOICES.find(choice => choice.value === value);
  return found ? found.label : String(value);
}

/** Citação no título: `null` é "não se aplica", e sai como traço. */
function titleCitationLabel(value: unknown): string {
  if (value === null || value === undefined) return "—";
  return value ? "Sim" : "Não";
}

function named(id: unknown, names: Record<string, string> | undefined): string {
  const key = String(id);
  return names?.[key] ?? `#${key}`;
}

function listChange(
  entry: { old?: unknown; new?: unknown },
  names: Record<string, string> | undefined
): string {
  const before = Array.isArray(entry.old) ? entry.old : [];
  const after = Array.isArray(entry.new) ? entry.new : [];
  const added = after.filter(id => !before.includes(id)).map(id => named(id, names));
  const removed = before.filter(id => !after.includes(id)).map(id => named(id, names));
  const parts: string[] = [];
  if (added.length) parts.push(`+ ${added.join(", ")}`);
  if (removed.length) parts.push(`− ${removed.join(", ")}`);
  if (!parts.length) return "sem mudança na lista";
  return parts.join(" · ");
}

/** O nome de um campo do diff em português — o mesmo das linhas. */
export function editFieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field;
}

/** O que uma linha de VOLTA DE VERSÃO diz além do diff (2026-10-31):
 *  pra qual versão foi. `null` em linha que não é volta. `formatWhen`
 *  vem de quem desenha, porque o drawer e a página formatam a data cada
 *  um do seu jeito. */
export function describeRevert(
  edit: Pick<ClassificationEdit, "kind" | "reverted_to_id" | "reverted_to_at">,
  formatWhen: (iso: string) => string
): string | null {
  if (edit.kind !== "revert") return null;
  if (edit.reverted_to_id == null) return "Voltou à versão original";
  return edit.reverted_to_at
    ? `Voltou à versão de ${formatWhen(edit.reverted_to_at)}`
    : `Voltou à versão #${edit.reverted_to_id}`;
}

/** Uma frase por campo do diff, na ordem em que o servidor gravou. */
export function describeEdit(edit: Pick<ClassificationEdit, "diff" | "labels">): EditLine[] {
  const labels = edit.labels ?? {};
  return Object.entries(edit.diff).map(([field, raw]) => {
    const entry = (raw ?? {}) as { old?: unknown; new?: unknown };
    const label = FIELD_LABELS[field] ?? field;
    let text: string;
    switch (field) {
      case "polarity":
        text = `${polarityLabel((entry.old as string | null) ?? null)}${ARROW}${polarityLabel((entry.new as string | null) ?? null)}`;
        break;
      case "approach":
        text = `${approachLabel(entry.old)}${ARROW}${approachLabel(entry.new)}`;
        break;
      case "theme_ids":
      case "key_message_ids":
      case "spokesperson_ids":
        text = listChange(entry, labels[field]);
        break;
      case "highlight_theme_id":
        text =
          entry.new === null || entry.new === undefined
            ? `destaque desfeito (era ${named(entry.old, labels[field])})`
            : `destaque em ${named(entry.new, labels[field])}`;
        break;
      case "title_citation":
        text = `${titleCitationLabel(entry.old)}${ARROW}${titleCitationLabel(entry.new)}`;
        break;
      case "reviewed":
        text = entry.new ? "marcada como revisada" : "revisão desmarcada";
        break;
      case "discarded":
        text = entry.new ? "descartada" : "restaurada";
        break;
      case "linked":
        text = "vinculada à mão a esta marca";
        break;
      case "theme_percentages":
        text = "pesos dos subtemas atualizados pela IA";
        break;
      default:
        text = `${JSON.stringify(entry.old ?? null)}${ARROW}${JSON.stringify(entry.new ?? null)}`;
    }
    return { field, label, text };
  });
}
